import c from 'classnames';
import React from 'react';
import {
    CANCELLATION_MODE,
    CANCELLATION_REASON_COLLECTION_MODE,
    CustomerPortal,
    CustomerPortalButtonSize,
} from '../types/customerPortal';
import { getContrastYIQ } from '../utils/color';
import CancellationForm from './CancellationForm';
import PoweredBy from './PoweredBy';

interface Props {
    customerPortal: CustomerPortal;
    user?: { id: string };
    subscription?: { id: string; status: string; cancel_at_period_end: boolean; cancel_at: string };
    disabled?: boolean;
    poweredBy?: boolean;
}

const CANCELLABLE_SUB_STATUSES = ['active', 'trialing', 'past_due'];

export default function CustomerPortalComponent({
    customerPortal: { id, designSettings, emailAuth: _emailAuth, features, mode },
    user,
    subscription,
    disabled = false,
    poweredBy = true,
}: Props) {
    const textColor = `text-${getContrastYIQ(designSettings.buttonColour)}`;
    const hasValidSubscription =
        subscription && CANCELLABLE_SUB_STATUSES.includes(subscription.status) && !subscription.cancel_at;
    const useEmailAuth = _emailAuth && !hasValidSubscription && !user;
    const showCancelReasonForm =
        !useEmailAuth &&
        features.subscription_cancel.enabled &&
        features.subscription_cancel?.cancellation_reason?.mode === CANCELLATION_REASON_COLLECTION_MODE.BEFORE &&
        (features.subscription_cancel?.cancellation_reason?.options || []).length > 0 &&
        (hasValidSubscription || disabled);

    return (
        <>
            {designSettings.customCSS && (
                <style type="text/css" dangerouslySetInnerHTML={{ __html: designSettings.customCSS }}></style>
            )}
            <form
                className={c('m-4 relative', { 'pointer-events-none': disabled })}
                id={`customerPortalForm${id}`}
                data-mode={mode}
            >
                <div
                    className={c('pricewell-customerportal-container flex', {
                        'space-x-2': !useEmailAuth && designSettings.layout === 'horizontal',
                        'flex-col space-y-4': !useEmailAuth && designSettings.layout === 'vertical',
                    })}
                    id={`customerPortalFormWrapper${id}`}
                >
                    {useEmailAuth && (
                        <input
                            id={`customerPortalFormEmail${id}`}
                            className={c(
                                'rounded-l-lg px-4 border-t mr-0 border-b border-l text-gray-800 border-gray-200 bg-white',
                                {
                                    'h-8 text-sm': designSettings.buttonSize === CustomerPortalButtonSize.SMALL,
                                    'h-16 text-base': designSettings.buttonSize === CustomerPortalButtonSize.MEDIUM,
                                    'h-32 text-xl': designSettings.buttonSize === CustomerPortalButtonSize.LARGE,
                                }
                            )}
                            placeholder={designSettings.placeholderText}
                            type="email"
                        />
                    )}
                    <button
                        className={c(
                            'pricewell-customerportal-button transition duration-500 ease-in-out transform shadow-xl focus:shadow-outline focus:outline-none overflow-hidden',
                            textColor,
                            {
                                'rounded-r-lg border': useEmailAuth,
                                'rounded-lg border-t border-b border-r': !useEmailAuth,
                                'blue-700': !designSettings.buttonColour,
                                'px-2 py-1 h-8 text-sm font-light':
                                    designSettings.buttonSize === CustomerPortalButtonSize.SMALL,
                                'px-8 py-2 h-16 text-base font-semibold':
                                    designSettings.buttonSize === CustomerPortalButtonSize.MEDIUM,
                                'px-16 py-2 h-32 text-xl font-semibold':
                                    designSettings.buttonSize === CustomerPortalButtonSize.LARGE,
                            }
                        )}
                        style={{
                            backgroundColor: designSettings.buttonColour,
                        }}
                        data-reasons={features.subscription_cancel.cancellation_reason.options.join(',')}
                        type="submit"
                        id={`customerPortal${id}`}
                    >
                        {designSettings.buttonText || 'Manage Subscription'}
                    </button>
                    {showCancelReasonForm && (
                        <button
                            className={c(
                                'pricewell-customerportal-button transition duration-500 ease-in-out transform shadow-xl focus:shadow-outline focus:outline-none overflow-hidden text-gray-600 rounded-lg border border-gray-200',
                                {
                                    'px-2 py-1 h-8 text-sm font-light':
                                        designSettings.buttonSize === CustomerPortalButtonSize.SMALL,
                                    'px-8 py-2 h-16 text-base font-semibold':
                                        designSettings.buttonSize === CustomerPortalButtonSize.MEDIUM,
                                    'px-16 py-2 h-32 text-xl font-semibold':
                                        designSettings.buttonSize === CustomerPortalButtonSize.LARGE,
                                }
                            )}
                            type="submit"
                            id={`customerPortalCancel${id}`}
                            data-success={
                                features.subscription_cancel.mode === CANCELLATION_MODE.PERIOD_END
                                    ? 'Next billing cancelled'
                                    : 'Subscription cancelled'
                            }
                            data-refresh={features.subscription_cancel.cancellation_reason.refresh}
                        >
                            {designSettings?.customWording?.cancelSubscription || 'Cancel Subscription'}
                        </button>
                    )}
                </div>
                <span
                    id={`customerPortalMessage${id}`}
                    className="pricewell-customerportal-message"
                    data-message-success={designSettings.messages.checkEmail}
                    data-message-invalid={designSettings.messages.invalidEmail}
                ></span>
                {poweredBy && <PoweredBy campaignId={id} offsetBottom={10} />}
            </form>
            {showCancelReasonForm && hasValidSubscription && subscription && (
                <CancellationForm id={id} features={features} subscriptionId={subscription.id} />
            )}
        </>
    );
}
